import { useEffect, useMemo, useReducer, useRef } from 'react'
import { Props, UploadFileItem } from '.'
import reducer, {
  initialState,
  setPreviewModalOptionAction,
  setUploadFileListAction,
} from './reducer'
import OSS from 'ali-oss'
import { message } from 'antd'
import { RcFile } from 'antd/lib/upload'
import { getBaseName } from '../../tools/path'
import { useOssStore } from '@/stores/ossSelect'
export const MAX_COUNT = 512

export function getOssClientInfo(accessKeyId: string, accessKeySecret: string) {
  return {
    ossClient: new OSS({
      // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
      accessKeyId: accessKeyId,
      accessKeySecret: accessKeySecret,
      // 从STS服务获取的安全令牌（SecurityToken）。
      // stsToken: 'yourSecurityToken',
      // 填写Bucket名称。
      // bucket: 'test-xhgj-xhmall-product',
      // bucket: 'xhgj-xhmall-product',
      bucket: process.env.REACT_APP_OSS_BUCKET,
      // 填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
      region: 'oss-cn-shanghai',
    }),
    timestamp: new Date().getTime(),
  }
}
export const imgPattern = /[^\s]+\.(jpg|jpeg|png|gif|bmp|webp|heic)$/i
/**
 * 判断文件后缀名是否为图片
 * @param filename
 */
export function isImage(filename: string): boolean {
  return imgPattern.test(filename)
}
/**
 * 获取表单上传成功的结果
 * @param list
 * @returns
 */
export function convertImgResultState(list: UploadFileItem[]) {
  return list
    .filter((item) => {
      return item.resultType === '成功'
    })
    .map((item) => {
      return {
        imgName: item.name,
        imgUrl: item?.osspath ?? '',
      }
    })
}
/**
 * 校验文件名是否符合命名规则
 * @param filename
 * @returns
 */
function isGoodsNameValid(filename: string) {
  const basename = getBaseName(filename)
  // z或xq+自然数
  // if (/^(z|xq)(0|[1-9]\d*)$/.test(basename)) {
  //   return true
  // }
  // 必须为数字-z数字，或数字-xq数字的形式
  if (/^\d+-(z|xq)\d+$/.test(basename)) {
    return true
  }
  return false
}
export function useInitOssHook() {
  const ossClientRef = useRef<{
    ossClient: OSS
    timestamp: number
  }>()
  const { accessKeyId, accessKeySecret } = useOssStore((state) => state)
  // http://xhgj-xhmall-product.oss-cn-shanghai.aliyuncs.com/
  function InitOssClient() {
    ossClientRef.current = getOssClientInfo(accessKeyId, accessKeySecret)
  }
  useEffect(() => {
    InitOssClient()
  }, [])
  return {
    ossClientRef,
  }
}
export function useSetupHook(props: Props) {
  const { maxCount = MAX_COUNT, onChange } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const { uploadFileList, previewModalOption } = state
  const uploadQueueRef = useRef<UploadFileItem[]>([])
  const { ossClientRef } = useInitOssHook()
  const statusList = useMemo(() => {
    let succeedCount = 0
    let failCount = 0

    uploadFileList.forEach((item) => {
      if (item.resultType === '成功') {
        succeedCount++
      } else if (item.resultType === '失败') {
        failCount++
      }
    })
    return {
      total: uploadFileList.length,
      progress: succeedCount + failCount,
      succeedCount,
      failCount,
    }
  }, [uploadFileList])

  const handleReUpload = (file: RcFile, fileList: RcFile[], uid: string) => {
    // 检测文件重名
    const fileNameSet = new Set(
      uploadQueueRef.current
        .filter((item) => {
          return item.uid !== file.uid
        })
        .map((item) => {
          return getBaseName(item.name)
        }),
    )

    //
    if (fileNameSet.has(getBaseName(file.name))) {
      message.error('和已上传的文件重名')
      return
    }

    // 找到对应的下标替换
    const reUploadIndex = uploadQueueRef.current.findIndex((item) => {
      return item.uid === uid
    })
    const baseItem = getValidatedFileItem(file)
    uploadQueueRef.current[reUploadIndex] = baseItem
    updateResultState()
    startUpload([uploadQueueRef.current[reUploadIndex].uid])
  }
  const handleBeforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (uploadQueueRef.current.length >= maxCount) {
      message.error(`图片数量已达到${maxCount}！不能继续上传`)
      return false
    }
    if (fileList.length + uploadQueueRef.current.length > maxCount) {
      message.error(`您选择的图片超过${maxCount}张，无法上传`)
      return false
    }
    const fileNameSet = new Set(
      uploadQueueRef.current.map((item) => {
        return getBaseName(item.name)
      }),
    )

    let hasDup = false
    const dupNameSet = new Set()
    const uploadUidList: string[] = []
    // 重名的图片直接覆盖，不计算后缀名
    const dupNameList: RcFile[] = []
    // 新增无重复的图片名
    const nodupFileList: RcFile[] = []
    for (const item of fileList) {
      const basename = getBaseName(item.name)
      // 这一步进行去重
      if (dupNameSet.has(basename)) {
        hasDup = true
        continue
      }
      dupNameSet.add(basename)
      if (fileNameSet.has(basename)) {
        dupNameList.push(item)
      } else {
        nodupFileList.push(item)
      }
    }
    if (dupNameList.length > 0) {
      hasDup = true
    }
    if (hasDup) {
      message.warning('您选择的图片中有重名图片，将自动覆盖')
      // 替换重名文件
      for (const i in uploadQueueRef.current) {
        const basename = getBaseName(uploadQueueRef.current[i].name)
        // 找到重名的下标，替换
        if (dupNameSet.has(basename)) {
          const baseItem = getValidatedFileItem(
            dupNameList.filter((item) => {
              return getBaseName(item.name) === basename
            })?.[0],
          )
          if (baseItem.resultType === '上传中') {
            uploadUidList.push(baseItem.uid)
          }
          uploadQueueRef.current[i] = baseItem
        }
      }
    }

    // 添加新文件到下载队列
    const newFileList: UploadFileItem[] = nodupFileList.map((item) => {
      const baseItem = getValidatedFileItem(item)
      if (baseItem.resultType === '上传中') {
        uploadUidList.push(baseItem.uid)
      }
      return baseItem
    })

    uploadQueueRef.current.push(...newFileList)
    updateResultState()

    startUpload(uploadUidList)

    return false
  }
  function getValidatedFileItem(item: RcFile): UploadFileItem {
    const baseItem: UploadFileItem = {
      // ..._.pick(item, fileProps),
      uid: item.uid,
      name: item.name,
      size: item.size,
      type: item.type,
      resultType: '上传中',
      originFile: item,
      url: '',
    }
    validateFile(baseItem)
    return baseItem
  }
  function validateFile(baseItem: UploadFileItem) {
    baseItem.resultType = '失败'
    // 校验文件格式
    if (!isImage(baseItem.name ?? '')) {
      baseItem.errorMessage = '不支持此文件格式'
      return
    }
    // 校验文件大小
    // 大于2mb则提示出错
    if ((baseItem.size ?? 0) > 2 << 20) {
      baseItem.errorMessage = '文件大小超出2m'
      return
    }
    if (!isGoodsNameValid(baseItem.name)) {
      baseItem.errorMessage = '图片不符合命名规则'
      return
    }
    baseItem.resultType = '上传中'
  }
  function updateResultState() {
    const newUploadFileList = [...uploadQueueRef.current]
    dispatch(setUploadFileListAction(newUploadFileList))
    onChange?.(newUploadFileList)
  }
  // 传入uid开始下载，避免重复执行
  async function startUpload(uidList: string[]) {
    if (uidList.length < 1) {
      return
    }
    for (let i = 0; i < uploadQueueRef.current.length; i++) {
      if (!uidList.includes(uploadQueueRef.current[i].uid)) {
        // 跳过不需要下载的uid
        continue
      }
      if (uploadQueueRef.current[i].resultType !== '上传中') {
        // 不是上传中的文件跳过
        continue
      }
      const newpath =
        `scp/front/${ossClientRef.current?.timestamp}/` +
        uploadQueueRef.current[i].name
      let res: any = {
        name: newpath,
      }
      try {
        res = await ossClientRef.current?.ossClient.put(
          newpath,
          uploadQueueRef.current[i].originFile,
        )
        // throw new Error('超时后')
      } catch (e) {
        uploadQueueRef.current[i].resultType = '失败'
        uploadQueueRef.current[i].errorMessage = '连接超时，请重试'
        updateResultState()
        // 出错后继续上传下一个
        continue
      }
      if (res?.res?.status !== 200) {
        uploadQueueRef.current[i].resultType = '失败'
        uploadQueueRef.current[i].errorMessage = '连接超时，请重试'
      } else {
        uploadQueueRef.current[i].resultType = '成功'
        uploadQueueRef.current[i].osspath = newpath
        uploadQueueRef.current[i].url = res.url
      }
      updateResultState()
    }
  }

  const handlePreview = (record: UploadFileItem) => {
    if (record.resultType !== '成功') {
      message.info('图片还没上传成功')
      return
    }
    dispatch(
      setPreviewModalOptionAction({
        fileUrl: decodeURIComponent(record.url),
        open: true,
      }),
    )
  }
  const cancelPreviewModal = () => {
    dispatch(
      setPreviewModalOptionAction({
        ...previewModalOption,
        open: false,
      }),
    )
  }

  return {
    // form,
    statusList,
    state,
    handleBeforeUpload,
    handleReUpload,
    handlePreview,
    cancelPreviewModal,
  }
}
import { useEffect, useMemo, useReducer, useRef } from 'react'
import { Props, UploadFileItem } from '.'
import reducer, {
  initialState,
  setPreviewModalOptionAction,
  setUploadFileListAction,
} from './reducer'
import OSS from 'ali-oss'
import { message } from 'antd'
import { RcFile } from 'antd/lib/upload'
import { getBaseName } from '../../tools/path'
import { useOssStore } from '@/stores/ossSelect'
export const MAX_COUNT = 512

export function getOssClientInfo(accessKeyId: string, accessKeySecret: string) {
  return {
    ossClient: new OSS({
      // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
      accessKeyId: accessKeyId,
      accessKeySecret: accessKeySecret,
      // 从STS服务获取的安全令牌（SecurityToken）。
      // stsToken: 'yourSecurityToken',
      // 填写Bucket名称。
      // bucket: 'test-xhgj-xhmall-product',
      // bucket: 'xhgj-xhmall-product',
      bucket: process.env.REACT_APP_OSS_BUCKET,
      // 填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
      region: 'oss-cn-shanghai',
    }),
    timestamp: new Date().getTime(),
  }
}
export const imgPattern = /[^\s]+\.(jpg|jpeg|png|gif|bmp|webp|heic)$/i
/**
 * 判断文件后缀名是否为图片
 * @param filename
 */
export function isImage(filename: string): boolean {
  return imgPattern.test(filename)
}
/**
 * 获取表单上传成功的结果
 * @param list
 * @returns
 */
export function convertImgResultState(list: UploadFileItem[]) {
  return list
    .filter((item) => {
      return item.resultType === '成功'
    })
    .map((item) => {
      return {
        imgName: item.name,
        imgUrl: item?.osspath ?? '',
      }
    })
}
/**
 * 校验文件名是否符合命名规则
 * @param filename
 * @returns
 */
function isGoodsNameValid(filename: string) {
  const basename = getBaseName(filename)
  // z或xq+自然数
  // if (/^(z|xq)(0|[1-9]\d*)$/.test(basename)) {
  //   return true
  // }
  // 必须为数字-z数字，或数字-xq数字的形式
  if (/^\d+-(z|xq)\d+$/.test(basename)) {
    return true
  }
  return false
}
export function useInitOssHook() {
  const ossClientRef = useRef<{
    ossClient: OSS
    timestamp: number
  }>()
  const { accessKeyId, accessKeySecret } = useOssStore((state) => state)
  // http://xhgj-xhmall-product.oss-cn-shanghai.aliyuncs.com/
  function InitOssClient() {
    ossClientRef.current = getOssClientInfo(accessKeyId, accessKeySecret)
  }
  useEffect(() => {
    InitOssClient()
  }, [])
  return {
    ossClientRef,
  }
}
export function useSetupHook(props: Props) {
  const { maxCount = MAX_COUNT, onChange } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const { uploadFileList, previewModalOption } = state
  const uploadQueueRef = useRef<UploadFileItem[]>([])
  const { ossClientRef } = useInitOssHook()
  const statusList = useMemo(() => {
    let succeedCount = 0
    let failCount = 0

    uploadFileList.forEach((item) => {
      if (item.resultType === '成功') {
        succeedCount++
      } else if (item.resultType === '失败') {
        failCount++
      }
    })
    return {
      total: uploadFileList.length,
      progress: succeedCount + failCount,
      succeedCount,
      failCount,
    }
  }, [uploadFileList])

  const handleReUpload = (file: RcFile, fileList: RcFile[], uid: string) => {
    // 检测文件重名
    const fileNameSet = new Set(
      uploadQueueRef.current
        .filter((item) => {
          return item.uid !== file.uid
        })
        .map((item) => {
          return getBaseName(item.name)
        }),
    )

    //
    if (fileNameSet.has(getBaseName(file.name))) {
      message.error('和已上传的文件重名')
      return
    }

    // 找到对应的下标替换
    const reUploadIndex = uploadQueueRef.current.findIndex((item) => {
      return item.uid === uid
    })
    const baseItem = getValidatedFileItem(file)
    uploadQueueRef.current[reUploadIndex] = baseItem
    updateResultState()
    startUpload([uploadQueueRef.current[reUploadIndex].uid])
  }
  const handleBeforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (uploadQueueRef.current.length >= maxCount) {
      message.error(`图片数量已达到${maxCount}！不能继续上传`)
      return false
    }
    if (fileList.length + uploadQueueRef.current.length > maxCount) {
      message.error(`您选择的图片超过${maxCount}张，无法上传`)
      return false
    }
    const fileNameSet = new Set(
      uploadQueueRef.current.map((item) => {
        return getBaseName(item.name)
      }),
    )

    let hasDup = false
    const dupNameSet = new Set()
    const uploadUidList: string[] = []
    // 重名的图片直接覆盖，不计算后缀名
    const dupNameList: RcFile[] = []
    // 新增无重复的图片名
    const nodupFileList: RcFile[] = []
    for (const item of fileList) {
      const basename = getBaseName(item.name)
      // 这一步进行去重
      if (dupNameSet.has(basename)) {
        hasDup = true
        continue
      }
      dupNameSet.add(basename)
      if (fileNameSet.has(basename)) {
        dupNameList.push(item)
      } else {
        nodupFileList.push(item)
      }
    }
    if (dupNameList.length > 0) {
      hasDup = true
    }
    if (hasDup) {
      message.warning('您选择的图片中有重名图片，将自动覆盖')
      // 替换重名文件
      for (const i in uploadQueueRef.current) {
        const basename = getBaseName(uploadQueueRef.current[i].name)
        // 找到重名的下标，替换
        if (dupNameSet.has(basename)) {
          const baseItem = getValidatedFileItem(
            dupNameList.filter((item) => {
              return getBaseName(item.name) === basename
            })?.[0],
          )
          if (baseItem.resultType === '上传中') {
            uploadUidList.push(baseItem.uid)
          }
          uploadQueueRef.current[i] = baseItem
        }
      }
    }

    // 添加新文件到下载队列
    const newFileList: UploadFileItem[] = nodupFileList.map((item) => {
      const baseItem = getValidatedFileItem(item)
      if (baseItem.resultType === '上传中') {
        uploadUidList.push(baseItem.uid)
      }
      return baseItem
    })

    uploadQueueRef.current.push(...newFileList)
    updateResultState()

    startUpload(uploadUidList)

    return false
  }
  function getValidatedFileItem(item: RcFile): UploadFileItem {
    const baseItem: UploadFileItem = {
      // ..._.pick(item, fileProps),
      uid: item.uid,
      name: item.name,
      size: item.size,
      type: item.type,
      resultType: '上传中',
      originFile: item,
      url: '',
    }
    validateFile(baseItem)
    return baseItem
  }
  function validateFile(baseItem: UploadFileItem) {
    baseItem.resultType = '失败'
    // 校验文件格式
    if (!isImage(baseItem.name ?? '')) {
      baseItem.errorMessage = '不支持此文件格式'
      return
    }
    // 校验文件大小
    // 大于2mb则提示出错
    if ((baseItem.size ?? 0) > 2 << 20) {
      baseItem.errorMessage = '文件大小超出2m'
      return
    }
    if (!isGoodsNameValid(baseItem.name)) {
      baseItem.errorMessage = '图片不符合命名规则'
      return
    }
    baseItem.resultType = '上传中'
  }
  function updateResultState() {
    const newUploadFileList = [...uploadQueueRef.current]
    dispatch(setUploadFileListAction(newUploadFileList))
    onChange?.(newUploadFileList)
  }
  // 传入uid开始下载，避免重复执行
  async function startUpload(uidList: string[]) {
    if (uidList.length < 1) {
      return
    }
    for (let i = 0; i < uploadQueueRef.current.length; i++) {
      if (!uidList.includes(uploadQueueRef.current[i].uid)) {
        // 跳过不需要下载的uid
        continue
      }
      if (uploadQueueRef.current[i].resultType !== '上传中') {
        // 不是上传中的文件跳过
        continue
      }
      const newpath =
        `scp/front/${ossClientRef.current?.timestamp}/` +
        uploadQueueRef.current[i].name
      let res: any = {
        name: newpath,
      }
      try {
        res = await ossClientRef.current?.ossClient.put(
          newpath,
          uploadQueueRef.current[i].originFile,
        )
        // throw new Error('超时后')
      } catch (e) {
        uploadQueueRef.current[i].resultType = '失败'
        uploadQueueRef.current[i].errorMessage = '连接超时，请重试'
        updateResultState()
        // 出错后继续上传下一个
        continue
      }
      if (res?.res?.status !== 200) {
        uploadQueueRef.current[i].resultType = '失败'
        uploadQueueRef.current[i].errorMessage = '连接超时，请重试'
      } else {
        uploadQueueRef.current[i].resultType = '成功'
        uploadQueueRef.current[i].osspath = newpath
        uploadQueueRef.current[i].url = res.url
      }
      updateResultState()
    }
  }

  const handlePreview = (record: UploadFileItem) => {
    if (record.resultType !== '成功') {
      message.info('图片还没上传成功')
      return
    }
    dispatch(
      setPreviewModalOptionAction({
        fileUrl: decodeURIComponent(record.url),
        open: true,
      }),
    )
  }
  const cancelPreviewModal = () => {
    dispatch(
      setPreviewModalOptionAction({
        ...previewModalOption,
        open: false,
      }),
    )
  }

  return {
    // form,
    statusList,
    state,
    handleBeforeUpload,
    handleReUpload,
    handlePreview,
    cancelPreviewModal,
  }
}
